// Item card text (Phaser-free). Lines are generated from the data — stats,
// effects, weapon modifiers, behaviour flags and affixes — so the numbers on a
// card can never drift from what the item does. Drawbacks go to `tradeoff`.

import { iconKey, mechKeys } from '../../art/keys';
import { art } from '../../art/registry';
import { COLORS, rarityColor, tagColor } from '../../config/palette';
import { getRun } from '../../core/run';
import type {
  EffectAction,
  EquipSlot,
  ItemDisplay,
  ItemEffect,
  ItemInstance,
  MajorUpgradeDef,
  MechId,
  Rarity,
  StatBlock,
  StatMod,
  StatusApplication,
  WeaponInstance,
} from '../../core/types';
import {
  EQUIPMENT,
  FLAG_TEXT,
  MAJOR_UPGRADES,
  MODS,
  MUTATIONS,
  SIGNATURE_EVOLUTION_TEXT,
  WEAPONS,
} from '../../data/items';
import { BASE_STATS } from '../../data/mechs';
import { combineModifiers, estimateDps, formatStatMod, isBeneficial } from './stats';

const RARITY_LABEL: Record<Rarity, string> = {
  standard: 'STANDARD',
  advanced: 'ADVANCED',
  prototype: 'PROTOTYPE',
};

export const SLOT_LABEL: Record<EquipSlot, string> = {
  chest: 'CHEST MODULE',
  shoulders: 'SHOULDER MODULE',
  back: 'BACK MODULE',
  legs: 'LEG MODULE',
};

/** Stats used for the nominal DPS printed on weapon cards (no crits, no gear). */
const NOMINAL: StatBlock = { ...BASE_STATS, critChance: 0 };

// ───────────────────────────── Small formatters ─────────────────────────────

/** 2.5 → '2.5', 3 → '3', 0.75 → '0.75'. */
function num(n: number): string {
  return String(Number(n.toFixed(2)));
}

function pct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function lowerFirst(s: string): string {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function statusText(s: StatusApplication): string {
  const stacks = s.stacks ?? 1;
  switch (s.id) {
    case 'burn':
      return `${plural(stacks, 'burn stack')}${s.potency ? ` (${num(s.potency)} dps, ${num(s.duration)}s)` : ` (${num(s.duration)}s)`}`;
    case 'shock':
      return `${plural(stacks, 'shock stack')} (${num(s.duration)}s)`;
    case 'slow':
      return `${s.potency ? pct(s.potency) + ' ' : ''}slow for ${num(s.duration)}s`;
    case 'vulnerable':
      return `vulnerable for ${num(s.duration)}s`;
    case 'stun':
      return `${num(s.duration)}s stun`;
    default:
      return `${s.id} for ${num(s.duration)}s`;
  }
}

const TRAIL_NAME: Record<string, string> = {
  electric: 'an electric',
  burn: 'a burning',
  plasma: 'a plasma',
  void: 'a void',
};

function triggerText(e: ItemEffect): string {
  switch (e.trigger) {
    case 'onDash':
      return 'On dash';
    case 'onDashEnd':
      return 'After a dash';
    case 'onJump':
      return 'On jump';
    case 'onLand':
      return 'Heavy landings';
    case 'onHit':
      return e.requiresTag ? `On ${e.requiresTag} hit` : 'On hit';
    case 'onKill':
      return e.requiresTag ? `On ${e.requiresTag} kill` : 'On kill';
    case 'onCrit':
      return 'On crit';
    case 'onOverheat':
      return 'On overheat';
    case 'onVentEnd':
      return 'After venting';
    case 'onDamaged':
      return 'When hit';
    case 'onShieldBreak':
      return 'Shield break';
    case 'onSignature':
      return 'On signature';
    case 'onUltimate':
      return 'On ultimate';
    case 'onFire':
      return 'On fire';
    case 'onRoomStart':
      return 'Room start';
    case 'onRoomClear':
      return 'Room clear';
    case 'tick':
      return `Every ${num(e.interval ?? 1)}s in combat`;
  }
}

function actionText(a: EffectAction): string {
  switch (a.type) {
    case 'explode':
      return `${a.radius} px blast for ${a.damage}`;
    case 'chain':
      return `lightning arcs to ${plural(a.count, 'enemy', 'enemies')} for ${a.damage}`;
    case 'shockwave':
      return `${a.radius} px shockwave for ${a.damage}`;
    case 'missiles':
      return `${plural(a.count, 'homing missile')}, ${a.damage} each`;
    case 'status':
      return `${statusText(a.status)}${a.radius ? ` within ${a.radius} px` : ''}`;
    case 'shield':
      return `restore ${a.amount} shield`;
    case 'heal':
      return `heal ${a.amount} HP`;
    case 'cool':
      return `vent ${pct(a.amount)} heat`;
    case 'trail':
      return `${TRAIL_NAME[a.kind] ?? 'a'} trail: ${a.dps} dps for ${num(a.duration)}s${a.status ? ` + ${a.status.id}` : ''}`;
    case 'gravity':
      return `${a.radius} px gravity well for ${num(a.duration)}s`;
    case 'cooldown':
      return `refund ${num(a.seconds)}s of ${a.ability} cooldown`;
    case 'buff':
      return `${lowerFirst(formatStatMod({ stat: a.stat, op: a.op, value: a.value }))} for ${num(a.duration)}s`;
    case 'custom':
      return a.id.replace(/_/g, ' ');
  }
}

/** One readable line per item effect, e.g. 'When hit: 150 px blast for 35 (1.5s cooldown)'. */
export function effectLine(e: ItemEffect): string {
  let body: string;
  if (e.action.type === 'trail' && (e.trigger === 'onDashEnd' || e.trigger === 'onDash')) {
    body = `Dashes leave ${actionText(e.action)}`;
  } else {
    body = `${triggerText(e)}: ${actionText(e.action)}`;
  }
  if (e.chance !== undefined && e.chance < 1) body += ` (${pct(e.chance)} chance)`;
  if (e.cooldown) body += ` (${num(e.cooldown)}s cooldown)`;
  return body;
}

function split(mods: readonly StatMod[] | undefined): { good: string[]; bad: string[] } {
  const good: string[] = [];
  const bad: string[] = [];
  for (const m of mods ?? []) (isBeneficial(m) ? good : bad).push(formatStatMod(m));
  return { good, bad };
}

function joinTradeoff(parts: (string | undefined)[]): string | undefined {
  const list = parts.filter((p): p is string => !!p && p.length > 0);
  return list.length ? list.join(' · ') : undefined;
}

// ───────────────────────────── Weapons / mods ─────────────────────────────

function weaponLines(w: WeaponInstance): string[] {
  const def = WEAPONS[w.defId];
  const m = combineModifiers(w.mods);
  const dmg = def.damage * m.damageMult;
  const rate = def.fireRate * m.fireRateMult;
  const dps = Math.round(estimateDps(w, NOMINAL));
  const lines: string[] = [];
  switch (def.behavior) {
    case 'auto':
      lines.push(`${num(dmg)} damage · ${num(rate)} shots/s · ~${dps} DPS`);
      break;
    case 'spread':
      lines.push(`${def.pellets} pellets × ${num(dmg)} · ${num(rate)} blasts/s · ~${dps} DPS up close`);
      lines.push(`Knockback ${Math.round(def.knockback * m.knockbackMult)} · range ${def.range} px`);
      break;
    case 'beam_chain':
      lines.push(`${num(dmg)} per tick · ${num(rate)} ticks/s · ~${dps} DPS`);
      if (def.chain) lines.push(`Chains to ${plural(def.chain.count, 'enemy', 'enemies')} within ${def.chain.range} px`);
      break;
    case 'charge':
      lines.push(`${Math.round(dmg)} damage at full charge (${num(def.chargeTime ?? 0.7)}s) · ~${dps} DPS`);
      lines.push(`Pierces ${plural(def.pierce + m.pierceAdd, 'enemy', 'enemies')}`);
      break;
    case 'missile_burst':
      lines.push(`${def.burst?.count ?? 1} homing missiles × ${num(dmg)} · ~${dps} DPS`);
      if (def.explode) lines.push(`Each bursts in a ${def.explode.radius} px blast`);
      break;
    case 'lobber':
      if (def.field) {
        lines.push(`${def.field.radius} px gravity well for ${num(def.field.duration)}s`);
        lines.push(`Implodes for ${Math.round(dmg * def.field.implodeDamageMult)} · ~${dps} DPS`);
      }
      break;
  }
  // Heat budget, the main limiter on sustained fire.
  if (def.behavior === 'charge') {
    const charges = Math.max(1, Math.floor(1 / Math.max(0.01, def.heatPerShot * (def.chargeTime ?? 0.7))));
    lines.push(`~${charges} full charges before overheating`);
  } else if (def.behavior === 'missile_burst' || def.behavior === 'lobber') {
    const shots = Math.max(1, Math.floor(1 / Math.max(0.01, def.heatPerShot * m.heatMult)));
    lines.push(`${plural(shots, def.behavior === 'lobber' ? 'grenade' : 'salvo')} before overheating`);
  } else {
    const secs = 1 / Math.max(0.001, def.heatPerShot * m.heatMult) / Math.max(0.01, rate * (m.burst?.count ?? 1));
    lines.push(`Overheats after ${num(Math.round(secs * 10) / 10)}s of fire`);
  }
  for (const mod of w.mods) lines.push(`Mod: ${MODS[mod.defId]?.name ?? mod.defId}`);
  const free = w.modSlots - w.mods.length;
  if (free > 0) lines.push(`${plural(free, 'free mod slot')}`);
  return lines;
}

/** Positive weapon-modifier lines for a mod card. */
function modLines(id: keyof typeof MODS): string[] {
  const w = MODS[id].weapon;
  const lines: string[] = [];
  if (w.burst) lines.push(`Fires ${w.burst.count}-round bursts`);
  if (w.extraProjectiles) lines.push(`Fires ${1 + w.extraProjectiles} projectiles per shot`);
  for (const s of w.addStatuses ?? []) {
    lines.push(s.id === 'burn' ? `Hits ignite: ${statusText(s)}` : `Hits apply ${statusText(s)}`);
  }
  if (w.explodeOnKill) lines.push(`Kills explode: ${w.explodeOnKill.radius} px, ${pct(w.explodeOnKill.damageMult)} of the hit`);
  if (w.damageMult && w.damageMult > 1) lines.push(`+${pct(w.damageMult - 1)} damage`);
  if (w.fireRateMult && w.fireRateMult > 1) lines.push(`+${pct(w.fireRateMult - 1)} fire rate`);
  if (w.heatMult && w.heatMult < 1) lines.push(`-${pct(1 - w.heatMult)} heat per shot`);
  if (w.coolMult && w.coolMult > 1) lines.push(`+${pct(w.coolMult - 1)} cooling`);
  if (w.projectileSpeedMult && w.projectileSpeedMult > 1) lines.push(`+${pct(w.projectileSpeedMult - 1)} projectile speed`);
  if (w.knockbackMult && w.knockbackMult > 1) lines.push(`+${pct(w.knockbackMult - 1)} knockback`);
  if (w.pierceAdd && w.pierceAdd > 0) lines.push(`Pierces ${plural(w.pierceAdd, 'extra enemy', 'extra enemies')}`);
  return lines;
}

// ───────────────────────────── Public ─────────────────────────────

function affixLines(item: ItemInstance): { good: string[]; bad: string[] } {
  const good: string[] = [];
  const bad: string[] = [];
  for (const a of item.affixes) (a.negative ? bad : good).push(a.label);
  return { good, bad };
}

function flagLines(flags: readonly string[] | undefined, mechId?: MechId): string[] {
  const out: string[] = [];
  for (const f of flags ?? []) {
    if (f === 'SIGNATURE_EVOLVED') {
      out.push(...signatureEvolutionLines(mechId));
      continue;
    }
    const text = FLAG_TEXT[f];
    if (text) out.push(text);
  }
  return out;
}

function currentMech(): MechId | undefined {
  try {
    return getRun()?.mechId;
  } catch {
    return undefined;
  }
}

function signatureEvolutionLines(mechId?: MechId): string[] {
  if (mechId) return [SIGNATURE_EVOLUTION_TEXT[mechId]];
  return (Object.keys(SIGNATURE_EVOLUTION_TEXT) as MechId[]).map((id) => `${id.toUpperCase()}: ${SIGNATURE_EVOLUTION_TEXT[id]}`);
}

/** Everything a card needs to draw an item (name, rarity colour, effect lines, tags, icon). */
export function describeItem(item: ItemInstance): ItemDisplay {
  const affix = affixLines(item);
  const base = {
    rarity: item.rarity,
    rarityLabel: RARITY_LABEL[item.rarity] ?? 'STANDARD',
    color: rarityColor(item.rarity),
  };
  switch (item.kind) {
    case 'weapon': {
      const def = WEAPONS[item.defId];
      return {
        ...base,
        name: def.name,
        kindLabel: 'WEAPON',
        short: def.short,
        lines: [...weaponLines(item), ...affix.good],
        tradeoff: joinTradeoff(affix.bad),
        tags: [...def.tags],
        icon: { key: art.weapons[item.defId]?.key ?? mechKeys.weapon(item.defId) },
      };
    }
    case 'equipment': {
      const def = EQUIPMENT[item.defId];
      const stats = split(def.stats);
      return {
        ...base,
        name: def.name,
        kindLabel: SLOT_LABEL[def.slot],
        short: def.short,
        lines: [...def.effects.map(effectLine), ...stats.good, ...affix.good],
        tradeoff: joinTradeoff([def.tradeoff ?? stats.bad.join(' · '), ...affix.bad]),
        tags: [...def.tags],
        icon: { key: art.visuals[item.defId]?.pieces[0]?.key ?? iconKey.slot(def.slot) },
      };
    }
    case 'mod': {
      const def = MODS[item.defId];
      return {
        ...base,
        name: def.name,
        kindLabel: def.altMode ? 'WEAPON MOD // ALT FIRE' : 'WEAPON MOD',
        short: def.short,
        lines: [...modLines(item.defId), ...affix.good],
        tradeoff: joinTradeoff([def.tradeoff, ...affix.bad]),
        tags: [...def.tags],
        icon: { key: iconKey.mod, tint: def.tags[0] ? tagColor(def.tags[0]) : COLORS.uiText },
      };
    }
    case 'mutation': {
      const def = MUTATIONS[item.defId];
      const stats = split(def.stats);
      return {
        ...base,
        name: def.name,
        kindLabel: def.mech ? `MUTATION // ${def.mech.toUpperCase()}` : 'MUTATION',
        short: def.short,
        lines: [...flagLines(def.flags), ...stats.good, ...def.effects.map(effectLine), ...affix.good],
        tradeoff: joinTradeoff([def.drawback, ...affix.bad]),
        tags: [...def.tags],
        icon: { key: iconKey.mutation, tint: def.aura ?? COLORS.orbUnstable },
      };
    }
  }
}

/** Display info for a boss-reward major upgrade. */
export function describeUpgrade(def: MajorUpgradeDef): ItemDisplay {
  const data = MAJOR_UPGRADES[def.id];
  const stats = split(def.stats);
  const mech = def.mech ?? currentMech();
  return {
    name: def.name,
    kindLabel: 'MAJOR UPGRADE',
    rarity: 'prototype',
    rarityLabel: def.mech ? `${def.mech.toUpperCase()} ONLY` : 'BOSS REWARD',
    color: COLORS.ultBar,
    short: data?.short ?? '',
    lines: [...flagLines(def.flags, mech), ...stats.good],
    tradeoff: joinTradeoff(stats.bad),
    tags: [],
    icon: { key: iconKey.upgrade },
  };
}
